/**
 * VM Status Model Interfaces
 *
 * Defines TypeScript interfaces and enums for VDI VM creation status tracking
 * Based on existing VdiTicketApproval entity in the backend
 */

export interface VmStatusModel {
  /** Ticket identifier */
  ticketId: string;

  /** Ticket number (e.g., "TN2510060002") */
  ticketNumber: string;

  /** Current approval status */
  approvalStatus: string;

  /** Current VM creation status */
  vmCreationStatus: VmCreationStatus;

  /** Azure VM name when created */
  vmName?: string;

  /** Azure VM size (e.g., "Standard_D2s_v3") */
  vmSize?: string;

  /** Public IP address when assigned */
  publicIpAddress?: string;

  /** Private IP address when assigned */
  privateIpAddress?: string;

  /** VM administrator username */
  adminUsername?: string;

  /** When VM creation started */
  vmCreationStartedAt?: string;

  /** When VM creation completed */
  vmCreationCompletedAt?: string;

  /** Error message if VM creation failed */
  failureMessage?: string;

  /** Error code for troubleshooting */
  failureCode?: string;

  /** User email who requested VDI */
  requesterEmail: string;

  /** User name extracted from email */
  requesterName?: string;

  /** VDI service description */
  serviceDescription?: string;

  /** Required software packages */
  softwareSet?: string;

  /** Whether GPU is required */
  gpuRequirement: boolean;

  /** VDI request justification */
  justification: string;

  /** Additional remarks */
  remarks?: string;

  /** User who approved the VDI request */
  approvedBy?: string;

  /** When approval was granted */
  approvedDate?: string;

  /** Estimated completion time for VM creation */
  estimatedCompletionTime?: string;

  /** Progress percentage (0-100) */
  progressPercentage: number;
}

/**
 * VM Creation Status Enumeration
 *
 * Represents the current state of VM creation process
 */
export enum VmCreationStatus {
  NotStarted = 'NotStarted',
  InProgress = 'InProgress',
  Succeeded = 'Succeeded',
  Failed = 'Failed'
}

/**
 * VM Status History Entry
 *
 * Represents a single status change in the VM creation timeline
 */
export interface VmStatusHistoryEntry {
  /** When the status change occurred */
  timestamp: string;

  /** Previous status before this change */
  previousStatus?: VmCreationStatus;

  /** New status after this change */
  newStatus: VmCreationStatus;

  /** Human-readable status message */
  statusMessage: string;

  /** Technical details for troubleshooting */
  technicalDetails?: string;

  /** User or system that triggered the change */
  updatedBy: string;

  /** Whether this was an automated status update */
  isAutomated: boolean;
}

/**
 * VM Status Display Data
 *
 * Processed data for UI component display
 */
export interface VmStatusDisplayData {
  /** Current VM creation status */
  currentStatus: VmCreationStatus;

  /** Status badge configuration */
  statusBadge: {
    text: string;
    color: string;
    icon: string;
  };

  /** Progress percentage for display */
  progressPercentage: number;

  /** User-friendly status message */
  statusMessage: string;

  /** Estimated completion time */
  estimatedCompletionTime?: string;

  /** Whether retry button should be shown */
  showRetryButton: boolean;

  /** Whether detailed VM information should be shown */
  showDetails: boolean;
}

/**
 * VM Status Badge Configuration
 *
 * Defines visual properties for each status type
 */
export interface VmStatusBadgeConfig {
  [VmCreationStatus.NotStarted]: {
    text: string;
    color: string;
    icon: string;
    message: string;
  };
  [VmCreationStatus.InProgress]: {
    text: string;
    color: string;
    icon: string;
    message: string;
  };
  [VmCreationStatus.Succeeded]: {
    text: string;
    color: string;
    icon: string;
    message: string;
  };
  [VmCreationStatus.Failed]: {
    text: string;
    color: string;
    icon: string;
    message: string;
  };
}

/**
 * VM Status Component State
 *
 * Internal state management for the VM status display component
 */
export interface VmStatusComponentState {
  /** Whether the component is currently loading data */
  isLoading: boolean;

  /** Current VM status data */
  vmStatus: VmStatusModel | null;

  /** Any error that occurred during data loading */
  error: string | null;

  /** Whether real-time polling is active */
  isPolling: boolean;

  /** When the status was last updated */
  lastUpdated: string;

  /** Whether status history is currently displayed */
  showHistory: boolean;

  /** Status history entries */
  history: VmStatusHistoryEntry[];
}

/**
 * VM Status Polling Configuration
 *
 * Configuration options for the polling service
 */
export interface VmStatusPollingConfig {
  /** Whether polling is enabled */
  enabled: boolean;

  /** Polling interval in milliseconds (default: 2 minutes) */
  interval: number;

  /** Maximum number of retry attempts */
  maxRetries: number;

  /** Delay between retry attempts in milliseconds */
  retryDelay: number;

  /** Request timeout in milliseconds */
  timeout: number;
}

/**
 * VM Status API Response
 *
 * API response format from backend endpoints
 */
export interface VmStatusApiResponse {
  /** Database record ID */
  id: number;

  /** Ticket identifier */
  ticketId: number;

  /** Ticket number */
  ticketNumber: string;

  /** Approval status */
  approvalStatus: string;

  /** VM creation status */
  vmCreationStatus: string;

  /** VM name */
  vmName?: string;

  /** VM size */
  vmSize?: string;

  /** Public IP address */
  publicIpAddress?: string;

  /** Private IP address */
  privateIpAddress?: string;

  /** Admin username */
  adminUsername?: string;

  /** When VM creation started */
  vmCreationStartedAt?: string;

  /** When VM creation completed */
  vmCreationCompletedAt?: string;

  /** Error message */
  failureMessage?: string;

  /** Error code */
  failureCode?: string;

  /** Requester email */
  requesterEmail: string;

  /** Requester name */
  requesterName?: string;

  /** Service description */
  serviceDescription?: string;

  /** Software set */
  softwareSet?: string;

  /** GPU requirement */
  gpuRequirement: boolean;

  /** Justification */
  justification: string;

  /** Remarks */
  remarks?: string;

  /** Approved by */
  approvedBy?: string;

  /** Approved date */
  approvedDate?: string;

  /** Progress percentage */
  progressPercentage: number;

  /** Estimated completion time */
  estimatedCompletionTime?: string;
}

/**
 * VM Status Retry Response
 *
 * Response format for VM creation retry API
 */
export interface VmStatusRetryResponse {
  /** Whether retry was successful */
  success: boolean;

  /** Response message */
  message: string;

  /** Current retry attempt number */
  retryAttempt: number;

  /** Estimated completion time */
  estimatedCompletionTime?: string;

  /** Current VM status */
  status?: string;
}

/**
 * VM Status Error Response
 *
 * Error response format for VM status APIs
 */
export interface VmStatusErrorResponse {
  /** Error details */
  error: {
    /** Error code */
    code: string;

    /** Error message */
    message: string;

    /** Additional error details */
    details?: string;

    /** Error timestamp */
    timestamp: string;
  };
}